import { useEffect, useRef, useState } from 'react'
import { marketAPI } from '../services/api'
import type { TickerQuote } from '../types'
import { formatPrice } from '../utils/marketPrice'
import CoinIcon from './CoinIcon'
import { displayBase } from '../utils/displaySymbol'

// Matches the endpoint's server-side cache TTL: polling faster would only
// re-read the same cached tape.
const POLL_INTERVAL_MS = 60_000

// Seconds of scroll per symbol. Duration is derived from the item count so the
// tape moves at a constant, readable speed regardless of how many symbols the
// backend returns.
//
// The universe is ~49 coins. At the old 3.5s a symbol a full cycle took nearly
// three minutes, so a coin at the far end of the tape was effectively
// unreachable -- you had to stare at the strip and wait. 1.6s a symbol is still
// a readable crawl (roughly a symbol and a half a second) and brings the whole
// tape round in about 80 seconds.
const SECONDS_PER_ITEM = 1.6

// Floor on the cycle length so a short tape (a venue outage leaving three
// quotes) whips past rather than looping every couple of seconds.
const MIN_DURATION_SECONDS = 24

const formatChange = (change: number): string =>
  `${change >= 0 ? '+' : ''}${change.toFixed(2)}%`

/** "BTC/USDT" -> "BTC", "MATIC/USDT" -> "POL". The quote currency is the same for every row. */
const baseAsset = (symbol: string): string => displayBase(symbol)

const TickerItem = ({ quote }: { quote: TickerQuote }) => {
  const change = quote.change_24h_pct
  // A venue that reported no 24h change renders neutral. Showing it as green
  // +0.00% would assert a flat market we have no data for.
  //
  // The gain/loss hues stay green and red, but at the 400 weight: on the dark
  // banner the 600 weights fall to roughly 3:1 contrast, which is below the
  // 4.5:1 these small numerals need.
  const changeClass =
    change === null ? 'text-gray-400' : change >= 0 ? 'text-green-400' : 'text-red-400'

  return (
    <span className="inline-flex items-center gap-2 px-4 whitespace-nowrap">
      <CoinIcon symbol={quote.symbol} size={16} />
      <span className="font-semibold text-white">{baseAsset(quote.symbol)}</span>
      <span className="tabular-nums text-gray-100">${formatPrice(quote.price)}</span>
      {/* change_24h_pct: say which window the move covers. */}
      <span className="text-[10px] uppercase tracking-wide text-gray-400">24h</span>
      <span className={`tabular-nums font-medium ${changeClass}`}>
        {change === null ? '—' : formatChange(change)}
      </span>
    </span>
  )
}

/**
 * Auto-scrolling strip of live prices, pinned to the top of the dashboard.
 *
 * Every symbol the backend returns is rendered and cycles past; nothing is
 * sliced. The strip used to freeze on hover, which -- on a sticky, full-width
 * bar sitting right under the header -- meant a resting cursor stopped the tape
 * indefinitely and only the handful of coins on screen were ever visible. The
 * pause is now keyboard-focus only (see index.css).
 *
 * Pure display: no click targets, no navigation. Polls the server-cached
 * /api/market/ticker endpoint once a minute.
 */
const TickerTape = () => {
  const [quotes, setQuotes] = useState<TickerQuote[]>([])
  const isMounted = useRef(true)

  useEffect(() => {
    isMounted.current = true

    const load = async () => {
      try {
        const tape = await marketAPI.getTicker()
        if (isMounted.current) {
          setQuotes(tape.tickers)
        }
      } catch (error) {
        // A failed poll leaves the previous prices on screen rather than
        // blanking the strip; the backend already serves its last good tape
        // when the venue is unreachable, so an error here is a transient
        // network blip and the next poll will recover.
        console.error('Ticker tape refresh failed:', error)
      }
    }

    load()
    const timer = setInterval(load, POLL_INTERVAL_MS)

    return () => {
      isMounted.current = false
      clearInterval(timer)
    }
  }, [])

  // Render nothing at all until there are prices — an empty grey bar above the
  // dashboard is worse than no bar.
  if (quotes.length === 0) {
    return null
  }

  return (
    <div
      // Bleeds past the <main> padding so the banner spans the full content
      // width edge to edge, and sticks below the mobile header (which the
      // layout fixes at h-16) or to the top of the viewport on desktop.
      //
      // Solid gray-900 rather than a tinted or translucent strip: anything
      // lighter reads as a stray band instead of a deliberate market bar. On
      // the dark terminal dashboard it drops a shade below the page ground so
      // it still reads as a separate bar.
      className="ticker-tape ticker-tape--continuous sticky top-16 md:top-0 z-20 -mx-4 md:-mx-8 -mt-6 mb-6
                 overflow-hidden border-b border-gray-700 bg-gray-900 py-3 text-sm
                 shadow-md dark:border-terminal-border dark:bg-[#0a0e12] dark:shadow-black/40"
      style={{
        ['--ticker-duration' as string]: `${Math.max(
          MIN_DURATION_SECONDS,
          quotes.length * SECONDS_PER_ITEM
        )}s`,
      }}
      aria-label="Live market prices"
    >
      <div className="ticker-track">
        {/* Two copies: the animation slides exactly one copy's width, so the
            second is what fills the gap the first leaves behind. Only the
            first is exposed to assistive tech. */}
        <div className="flex items-center">
          {quotes.map((quote) => (
            <TickerItem key={quote.symbol} quote={quote} />
          ))}
        </div>
        {/* The seam filler. Hidden under reduced motion, where the track does
            not scroll and the strip is read by scrolling it by hand -- one copy
            of the list is enough there, and two would just repeat themselves. */}
        <div className="ticker-copy flex items-center" aria-hidden="true">
          {quotes.map((quote) => (
            <TickerItem key={`${quote.symbol}-duplicate`} quote={quote} />
          ))}
        </div>
      </div>
    </div>
  )
}

export default TickerTape
